import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import WorkflowStatusFilter, {
  WorkflowChip,
  effectiveWorkflow,
} from '@/components/WorkflowStatusChips';
import { Card } from '@/components/ui/card';
import HistoricoPedido from '@/components/HistoricoPedido';
import { PageCover, PageBody, CoverButton, CoverSearch, SheetHeader, Segmented, StatusDot, PERIODOS, legendaPeriodo } from '@/components/Page';
import { formatBRL, formatBRLCompacto, formatData, formatDataHora, formatHora, formatNumero } from '@/lib/format';
import { Plus, CheckCircle, Truck, CreditCard, Link2, Edit, RefreshCw, Trash2, FileText, Upload, History, Eye, Loader2, ChevronDown, SplitSquareHorizontal, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/ConfirmDialog';
import { abrirComprovante } from '@/components/ComprovantesPedido';
import { AcoesParteBelluno, EstadoLinkBelluno, gerarLinkBelluno } from '@/components/LinkBelluno';
import { comRespostaBelluno, linkBellunoAtivo, podeCobrarBelluno } from '@/lib/belluno';
import { nomeFilial } from '@/lib/filiais';
import {
  FINANCIAMENTO, FORMAS_COM_LINK, FORMAS_MANUAIS, aguardandoFinanceira, metodoDaForma, nomeDaForma, opcoesDeParcelas,
  resumoFinanciamento, ultimaTentativaReprovada,
} from '@/lib/pagamento';

// Status do workflow vem de @/components/WorkflowStatusChips (fonte única com a Gestão e o Dashboard).
// Formas de pagamento (nomes, as que exigem comprovante, o método das partes) vêm de @/lib/pagamento.

const MAX_COMPROVANTE_BYTES = 10 * 1024 * 1024;

// Pedidos já criados no Tiny: excluir no ERP não cancela lá (a confirmação de exclusão avisa).
const numerosNoTiny = (o) => [...new Set([o.tiny_pedido_numero,
  ...(o.tiny_pedidos || []).filter((t) => t.status === 'criado').map((t) => t.numero_tiny)].filter(Boolean))];

const METODO_OPCOES = [
  { value: 'credit_card', label: 'Cartão' },
  { value: 'pix', label: 'PIX' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'belluno', label: 'Belluno' },
];
const NOME_DO_METODO = Object.fromEntries(METODO_OPCOES.map((o) => [o.value, o.label]));
// Teto de parcelas do link Belluno de uma parte: 13x a 21x, como o pedido em Belluno.
const PARCELAS_BELLUNO = opcoesDeParcelas('belluno');

// Método inicial das partes: herda a forma do PEDIDO. Um pedido criado como
// PIX que vai ser dividido quase sempre vira PIX+PIX — nascer em "Cartão"
// obrigava o vendedor a trocar as duas partes na mão. Boleto ou Belluno
// desligados (`habilitados`, do GET /config/pagamento) começam em cartão.
const metodoInicial = (formaPedido, habilitados) => {
  const m = metodoDaForma(formaPedido) || 'credit_card';
  return m in habilitados && !habilitados[m] ? 'credit_card' : m;
};

// Opções do select de método por parte. Boleto só entra quando a conta
// Pagar.me o habilita e Belluno com a integração ligada: oferecer o que o
// backend recusa só rende erro ao vendedor. O método JÁ salvo entra sempre: um
// <select> cujo `value` não casa com nenhuma <option> perde a seleção e trava
// (foi o bug do split PIX+PIX).
const opcoesMetodo = (atual, habilitados) =>
  METODO_OPCOES.filter((o) => !(o.value in habilitados) || habilitados[o.value] || o.value === atual);

// "cartão, PIX ou Belluno": as formas que cada parte aceita, na frase de ajuda.
const fraseDosMetodos = (habilitados) => {
  const nomes = opcoesMetodo(null, habilitados).map((o) => ({ credit_card: 'cartão', boleto: 'boleto' }[o.value] || o.label));
  return `${nomes.slice(0, -1).join(', ')} ou ${nomes[nomes.length - 1]}`;
};

const CLASSE_SELECT_PARTE = 'mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-card px-2 py-1.5 text-xs text-slate-700 dark:text-slate-200 outline-none focus:border-blue-500/70 focus:ring-2 focus:ring-blue-500/20';

// O método de uma parte e, se for Belluno, o teto de parcelas do link dela.
function MetodoDaParte({ n, metodo, onMetodo, parcelas, onParcelas, habilitados }) {
  return (
    <>
      <select value={metodo} onChange={(e) => onMetodo(e.target.value)} data-testid={`split-metodo-${n}`} aria-label={`Forma do pagamento ${n}`} className={CLASSE_SELECT_PARTE}>
        {opcoesMetodo(metodo, habilitados).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {metodo === 'belluno' && (
        <select value={parcelas} onChange={(e) => onParcelas(Number(e.target.value))} data-testid={`split-parcelas-${n}`} aria-label={`Parcelas do pagamento ${n}`} className={CLASSE_SELECT_PARTE}>
          {PARCELAS_BELLUNO.map((x) => <option key={x} value={x}>até {x}x</option>)}
        </select>
      )}
    </>
  );
}

// Controle compacto de gerar checkout: popover com "Pagamento único" (imediato)
// ou "Dividir em 2 pagamentos" (2 inputs; pagamento 2 = total - pagamento 1,
// derivado). Cada parte o cliente paga como quiser: cartão parcelado, pix, boleto
// ou o link da Belluno (13x a 21x). No pedido em Belluno o pagamento único é o
// link da Belluno no valor cheio (`rotulo` "Gerar link Belluno").
// `checkoutAtivo`: já existe um link aguardando pagamento. O controle deixa
// de ser "gerar" e vira "alterar a forma" — trocar o modo INVALIDA o link
// anterior no backend (só um caminho de cobrança por pedido, sempre).
function GerarCheckoutControl({
  total, onSingle, onSplit, checkoutAtivo = false, isSplit = false, partesAtuais = [], habilitados = {}, formaPedido = null,
  parcelamentoPedido = 1, rotulo = 'Gerar checkout', testid,
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(() => (isSplit ? 'split' : 'single')); // 'single' | 'split'
  // Reabrir "Alterar forma" num split existente pré-carrega valores e métodos
  // salvos — sem isso, regenerar resetava PIX->Cartão sem o vendedor notar.
  const [valor1, setValor1] = useState(() =>
    (partesAtuais[0]?.valor != null ? Number(partesAtuais[0].valor) : total / 2).toFixed(2));
  const padraoMetodo = metodoInicial(formaPedido, habilitados);
  // Parcelas da parte Belluno: as já escolhidas na parte ou as do pedido em Belluno (senão 13x, como o formulário
  // do pedido).
  const padraoParcelas = PARCELAS_BELLUNO.includes(Number(parcelamentoPedido)) ? Number(parcelamentoPedido) : PARCELAS_BELLUNO[0];
  const parcelasDe = (parte) => (parte?.metodo === 'belluno' && PARCELAS_BELLUNO.includes(parte.parcelamento) ? parte.parcelamento : padraoParcelas);
  const [metodo1, setMetodo1] = useState(partesAtuais[0]?.metodo || padraoMetodo);
  const [metodo2, setMetodo2] = useState(partesAtuais[1]?.metodo || padraoMetodo);
  const [parcelas1, setParcelas1] = useState(() => parcelasDe(partesAtuais[0]));
  const [parcelas2, setParcelas2] = useState(() => parcelasDe(partesAtuais[1]));
  const [busy, setBusy] = useState(false);

  const v1 = Math.min(Math.max(parseFloat(valor1) || 0, 0), total);
  const v2 = Math.max(total - v1, 0); // cartão 2 fecha a conta sempre
  const splitValido = v1 > 0 && v2 > 0;

  const reset = () => {
    setMode(isSplit ? 'split' : 'single');
    setValor1((partesAtuais[0]?.valor != null ? Number(partesAtuais[0].valor) : total / 2).toFixed(2));
    setMetodo1(partesAtuais[0]?.metodo || padraoMetodo);
    setMetodo2(partesAtuais[1]?.metodo || padraoMetodo);
    setParcelas1(parcelasDe(partesAtuais[0]));
    setParcelas2(parcelasDe(partesAtuais[1]));
  };

  const doSingle = async () => {
    setBusy(true);
    try { await onSingle(); setOpen(false); reset(); } finally { setBusy(false); }
  };

  const doSplit = async () => {
    setBusy(true);
    try {
      // arredonda p/ 2 casas; cartão 2 absorve o resto p/ soma == total exata
      const c1 = Math.round(v1 * 100) / 100;
      const c2 = Math.round((total - c1) * 100) / 100;
      // A parte Belluno leva o teto de parcelas do link dela.
      const parte = (valor, metodo, parcelas) => (metodo === 'belluno' ? { valor, metodo, parcelas } : { valor, metodo });
      await onSplit([parte(c1, metodo1, parcelas1), parte(c2, metodo2, parcelas2)]);
      setOpen(false);
      reset();
    } catch { /* toast já disparado no handler; mantém o popover aberto */ } finally { setBusy(false); }
  };

  return (
    // Recomeça ao abrir e ao fechar: os padrões dependem da config de pagamento, que pode chegar depois da lista.
    <Popover open={open} onOpenChange={(o) => { setOpen(o); reset(); }}>
      <PopoverTrigger asChild>
        {checkoutAtivo ? (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => e.stopPropagation()}
            className="dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:border-slate-600"
            data-testid={testid}
          >
            Alterar forma
            <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-70" />
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={(e) => e.stopPropagation()}
            data-testid={testid}
          >
            <Link2 className="h-4 w-4 mr-1.5" />
            {rotulo}
            <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-80" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80"
        onClick={(e) => e.stopPropagation()}
      >
        {checkoutAtivo && (
          <p className="mb-2.5 rounded-lg bg-sheet2 px-2.5 py-2 text-[12.5px] leading-snug text-gray-600 dark:text-gray-300">
            Já existe um link aguardando pagamento. Alterar a forma invalida
            o link enviado ao cliente e gera outro no lugar.
          </p>
        )}
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => setMode('single')}
            className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-left transition-colors ${
              mode === 'single'
                ? 'bg-accent font-medium text-foreground'
                : 'text-gray-600 hover:bg-accent dark:text-gray-300'
            }`}
          >
            <Link2 className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">Pagamento único</span>
          </button>
          <button
            type="button"
            onClick={() => setMode('split')}
            className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-left transition-colors ${
              mode === 'split'
                ? 'bg-accent font-medium text-foreground'
                : 'text-gray-600 hover:bg-accent dark:text-gray-300'
            }`}
          >
            <SplitSquareHorizontal className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">Dividir em 2 pagamentos</span>
          </button>
        </div>

        {mode === 'single' ? (
          <Button
            onClick={doSingle}
            disabled={busy}
            size="sm"
            className="mt-3 w-full"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
            Gerar e copiar link
          </Button>
        ) : (
          <div className="mt-3 space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">Pagamento 1</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500">R$</span>
                  <input
                    type="number"
                    min="0"
                    max={total}
                    step="0.01"
                    value={valor1}
                    onChange={(e) => setValor1(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-card pl-8 pr-2.5 py-2 text-sm text-slate-800 dark:text-slate-100 tabular-nums outline-none focus:border-blue-500/70 focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <MetodoDaParte n={1} metodo={metodo1} onMetodo={setMetodo1} parcelas={parcelas1} onParcelas={setParcelas1} habilitados={habilitados} />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">Pagamento 2</label>
                <div className="flex h-[38px] items-center rounded-lg border border-slate-200/70 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 px-2.5 text-sm text-slate-600 dark:text-slate-300 tabular-nums">
                  {formatBRL(v2)}
                </div>
                <MetodoDaParte n={2} metodo={metodo2} onMetodo={setMetodo2} parcelas={parcelas2} onParcelas={setParcelas2} habilitados={habilitados} />
              </div>
            </div>
            <p className="text-[12px] text-gray-500">
              Soma sempre {formatBRL(total)}. O pagamento 2 recebe o restante. Cada parte: {fraseDosMetodos(habilitados)}.
            </p>
            <Button
              onClick={doSplit}
              disabled={busy || !splitValido}
              size="sm"
              className="w-full"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SplitSquareHorizontal className="h-4 w-4 mr-2" />}
              Gerar 2 links
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Seção compacta de split no card: uma linha fina por parte (valor + status +
// copiar link). Sem caixas aninhadas — só hairline entre as linhas.
// Parte PIX/boleto pendente ganha "Anexar comprovante" na própria linha — o
// comprovante nasce marcado com o pagamento a que pertence e o financeiro
// aprova parte a parte (a tarefa no Bitrix é criada no upload, como sempre).
// Parte Belluno mostra o link dela (copiar, consultar, cancelar ou um novo).
function SplitSection({ pedido, partes, onCopy, podeAnexar = false, onAnexar, uploading = false, podeAgirBelluno = false, onBelluno, onErroBelluno }) {
  return (
    <div className="mt-3 border-t border-slate-200/70 dark:border-slate-800 pt-3">
      <p className="mb-1.5 text-[12.5px] font-medium text-gray-500">
        Pagamento dividido
      </p>
      <div className="divide-y divide-slate-200/60 dark:divide-slate-800/70">
        {partes.map((p) => {
          const isManualParte = p.metodo === 'pix' || p.metodo === 'boleto';
          // Belluno: o teto de parcelas do link enquanto aberto; paga, as parcelas que o cliente escolheu.
          const parcelasBelluno = p.metodo !== 'belluno' ? ''
            : !p.pago ? ` até ${p.parcelamento}x` : p.parcelamento > 1 ? ` em ${p.parcelamento}x` : '';
          return (
          <div key={p.indice} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1.5" data-testid={`parte-${p.indice}`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm text-slate-500 dark:text-slate-400">Pagamento {p.indice}</span>
              <span className="text-sm font-semibold text-gray-800 dark:text-slate-100 tabular-nums">{formatBRL(p.valor)}</span>
              <span className="text-[12px] text-gray-500">
                {NOME_DO_METODO[p.metodo] || 'Cartão'}{parcelasBelluno}
              </span>
            </div>
            {p.metodo === 'belluno' ? (
              <div className="flex items-center gap-2">
                <AcoesParteBelluno pedido={pedido} parte={p.indice} podeAgir={podeAgirBelluno} onChanged={onBelluno} onErro={onErroBelluno} />
              </div>
            ) : (
            <div className="flex items-center gap-2">
              {!p.pago && isManualParte && podeAnexar && (
                <label
                  title={`Anexar comprovante do pagamento ${p.indice}`}
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors ${
                    uploading
                      ? 'bg-slate-100 text-slate-400 dark:bg-slate-800 cursor-not-allowed'
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20 cursor-pointer'
                  }`}
                >
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  Comprovante
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,application/pdf"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      onAnexar?.(p, f);
                      e.target.value = '';
                    }}
                    data-testid={`upload-comprovante-parte-${p.indice}`}
                  />
                </label>
              )}
              {p.pago ? (
                <StatusDot tone="ok" className="text-[12.5px]">Pago</StatusDot>
              ) : (
                <StatusDot tone="run" className="text-[12.5px]">Aguardando</StatusDot>
              )}
              {p.checkout_url && (
                <button
                  type="button"
                  onClick={() => onCopy(p.checkout_url)}
                  title="Copiar link deste pagamento"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 hover:bg-blue-500/10 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

const Orders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  // Busca por número do pedido OU cliente — vai ao SERVIDOR (a lista local é
  // só a página de 50; o alvo pode ser mais antigo que isso). Pré-preenchida
  // por ?busca= — o card da proposta convertida aponta para /pedidos?busca=PED-….
  const [numeroBusca, setNumeroBusca] = useState(searchParams.get('busca') || '');
  // Recorte de data da lista (mesma régua dos KPIs do Dashboard).
  const [periodo, setPeriodo] = useState('tudo');
  // Boleto depende da conta Pagar.me e a parte Belluno da integração ligada — a UI não oferece o que o backend recusa.
  const [habilitados, setHabilitados] = useState({ boleto: false, belluno: false });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  // Números da capa: base inteira do período (o backend já recorta pelo vendedor quando é comercial).
  const [kpis, setKpis] = useState(null);
  useEffect(() => {
    let ativo = true;
    axios
      .get(`${API_URL}/pedidos/kpis`, { params: { periodo } })
      .then((r) => ativo && setKpis(r.data))
      .catch(() => ativo && setKpis(null));
    return () => { ativo = false; };
  }, [periodo, lastUpdate]);

  // Workflow / comprovantes
  const [uploadingId, setUploadingId] = useState(null);
  const [viewingFileId, setViewingFileId] = useState(null);
  const [expandedHistorico, setExpandedHistorico] = useState({});

  const fetchOrders = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      // Busca tudo e filtra client-side: troca de chip instantânea (sem refetch)
      // e contagem por status sempre visível no filtro. O número do pedido é a
      // exceção — filtra no servidor para achar pedidos além da página de 50.
      const params = {};
      if (numeroBusca.trim()) params.busca = numeroBusca.trim();
      if (periodo !== 'tudo') params.periodo = periodo;
      const response = await axios.get(`${API_URL}/pedidos`, { params });

      if (silent) {
        setOrders(prevOrders => {
          response.data.forEach(newOrder => {
            const oldOrder = prevOrders.find(o => o.id === newOrder.id);
            if (oldOrder && oldOrder.payment_status !== newOrder.payment_status) {
              if (newOrder.payment_status === 'confirmed' || newOrder.payment_status === 'paid') {
                toast.success(`Pedido ${newOrder.numero_pedido} foi pago!`, { duration: 5000 });
              }
            }
          });
          return response.data;
        });
      } else {
        setOrders(response.data);
      }

      setLastUpdate(new Date());

      if (silent) {
        setAutoRefreshing(true);
        setTimeout(() => setAutoRefreshing(false), 1000);
      }
    } catch {
      if (!silent) {
        toast.error('Erro ao carregar pedidos');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [numeroBusca, periodo]);

  useEffect(() => {
    // Debounce só quando há busca digitada — carga inicial permanece imediata.
    const delay = numeroBusca ? 350 : 0;
    const timeoutId = setTimeout(() => fetchOrders(), delay);
    const intervalId = setInterval(() => {
      fetchOrders(true);
    }, 30000);
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [fetchOrders, numeroBusca]);

  useEffect(() => {
    axios.get(`${API_URL}/config/pagamento`)
      .then((r) => setHabilitados({ boleto: Boolean(r.data?.boleto_habilitado), belluno: Boolean(r.data?.belluno_habilitado) }))
      .catch(() => {});  // flag off é o padrão seguro: some a opção, não quebra a tela
  }, []);

  // Contagem por status (mostrada nos chips) + lista visível pós-filtro.
  const statusCounts = useMemo(() => {
    const counts = { all: orders.length };
    orders.forEach((o) => {
      const wf = effectiveWorkflow(o) || 'aguardando_pagamento';
      counts[wf] = (counts[wf] || 0) + 1;
    });
    return counts;
  }, [orders]);

  const visibleOrders = useMemo(
    () => (statusFilter === 'all' ? orders : orders.filter((o) => effectiveWorkflow(o) === statusFilter)),
    [orders, statusFilter]
  );

  const getStatusConfig = (status) => {
    const configs = {
      draft: { label: 'Rascunho' },
      pending: { label: 'Pendente' },
      approved: { label: 'Aprovado' },
      processing: { label: 'Processando' },
      shipped: { label: 'Enviado' },
      delivered: { label: 'Entregue' },
      cancelled: { label: 'Cancelado' },
    };
    return configs[status] || configs.pending;
  };

  // Aplica o resultado do gerar-checkout no pedido em memória (checkout_token,
  // e — no caso split — o array pagamento_split), sem refetch.
  const patchOrder = (orderId, patch) => {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...patch } : o)));
  };

  // Resposta das rotas da Belluno (link do pedido ou de uma parte, reconsulta, cancelamento) no pedido em memória.
  const aplicarBelluno = (orderId) => (d) => {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? comRespostaBelluno(o, d) : o)));
  };

  // Pagamento único do pedido em Belluno: o link da Belluno no valor cheio (desfaz um split sem pagamento).
  const handleGerarLinkBelluno = async (order) => {
    const d = await gerarLinkBelluno(order);
    if (d) aplicarBelluno(order.id)(d);
    else fetchOrders(true); // a Belluno pode ter falhado depois de desfazer o split: a tela relê o pedido
  };

  // Pagamento único (comportamento atual): POST sem partes, copia URL, toast.
  const handleGerarCheckout = async (order) => {
    try {
      const response = await axios.post(`${API_URL}/pedidos/${order.id}/gerar-checkout`, {});
      const url = response.data.checkout_url;
      // Regenerar como único limpa um split não pago (contrato do backend).
      patchOrder(order.id, { checkout_token: response.data.checkout_token, pagamento_split: null });
      await navigator.clipboard.writeText(url);
      toast.success('Link de checkout copiado! Envie ao cliente.');
    } catch (error) {
      fetchOrders(true); // links Belluno das partes podem ter caído antes do erro
      const msg = error.response?.data?.detail || 'Erro ao gerar link de checkout';
      toast.error(msg);
    }
  };

  // Split em 2 pagamentos: POST { partes: [{valor, metodo}, {valor, metodo}] } → grava
  // pagamento_split no pedido. Backend valida soma == total e 400a se já pago.
  const handleGerarSplit = async (order, partes) => {
    try {
      const response = await axios.post(`${API_URL}/pedidos/${order.id}/gerar-checkout`, { partes });
      // Backend devolve checkout_token: null no split (mata o link de valor
      // cheio). Espelhar isso é o que faz a UI parar de oferecer os dois.
      if (!Array.isArray(response.data.split) || response.data.split.length !== 2) {
        // Backend divergente devolveu link único: avisar em vez de fingir sucesso
        toast.error('O servidor não gerou o split. Recarregue a página (Ctrl+F5) e tente de novo.');
        throw new Error('resposta sem split');
      }
      patchOrder(order.id, {
        checkout_token: response.data.checkout_token ?? null,
        pagamento_split: response.data.split,
      });
      toast.success('2 links de pagamento gerados!');
    } catch (error) {
      fetchOrders(true); // links Belluno antigos podem ter caído antes do erro
      const msg = error.response?.data?.detail || 'Erro ao gerar os 2 links';
      toast.error(msg);
      throw error; // deixa o popover saber que falhou
    }
  };

  // O doc do pedido guarda só o token; a URL é montada na origem atual (o
  // checkout é servido pelo mesmo domínio do painel).
  const checkoutUrlDe = (order) =>
    order.checkout_token ? `${window.location.origin}/checkout/${order.checkout_token}` : null;

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado! Envie ao cliente.');
    } catch {
      toast.error('Não foi possível copiar o link');
    }
  };

  const handleOpenDeleteDialog = (order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setOrderToDelete(null);
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;

    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/pedidos/${orderToDelete.id}`);

      // Remover pedido da lista
      setOrders(prevOrders => prevOrders.filter(order => order.id !== orderToDelete.id));

      toast.success('Pedido deletado com sucesso!');
      handleCloseDeleteDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao deletar pedido';
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const toggleHistorico = (orderId) => {
    setExpandedHistorico((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  // `parte` (opcional): num split, a parte do pagamento a que o comprovante
  // pertence — vai marcado ao backend e o financeiro aprova parte a parte.
  const handleUploadComprovante = async (order, file, parte = null) => {
    if (!file) return;
    if (file.size > MAX_COMPROVANTE_BYTES) {
      toast.error('Arquivo excede o limite de 10 MB');
      return;
    }
    setUploadingId(order.id);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (parte) {
        formData.append('parte', parte.indice);
        formData.append('forma_pagamento', parte.metodo);
      } else if (order.forma_pagamento) {
        formData.append('forma_pagamento', order.forma_pagamento);
      }
      await axios.post(`${API_URL}/pedidos/${order.id}/comprovantes`, formData);
      toast.success('Comprovante anexado! Aguardando conferência do financeiro.');
      await fetchOrders(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao anexar comprovante');
    } finally {
      setUploadingId(null);
    }
  };

  const handleViewComprovante = async (order, comp) => {
    setViewingFileId(comp.file_id);
    await abrirComprovante(order.id, comp);
    setViewingFileId(null);
  };

  return (
    <div data-testid="orders-page">
      <PageCover
        title="Pedidos"
        subtitle={lastUpdate ? `${legendaPeriodo(periodo)} · atualizado às ${formatHora(lastUpdate)}` : legendaPeriodo(periodo)}
        actions={
          <>
            <CoverSearch value={numeroBusca} onChange={setNumeroBusca} placeholder="Número do pedido ou cliente" data-testid="filtro-numero-pedido" />
            <CoverButton onClick={() => fetchOrders()} disabled={loading}>
              <RefreshCw className={loading || autoRefreshing ? 'animate-spin' : ''} />
              Atualizar
            </CoverButton>
            <CoverButton primary onClick={() => navigate('/pedidos/novo')} data-testid="new-order-button">
              <Plus />
              Novo pedido
            </CoverButton>
          </>
        }
        figures={kpis ? [
          { value: formatBRLCompacto(kpis.valor_total), label: 'vendidos no período' },
          { value: formatNumero(kpis.total), label: kpis.total === 1 ? 'pedido' : 'pedidos' },
          { value: formatNumero(kpis.pagos), label: 'pagos' },
          { value: formatNumero(kpis.abertos), label: 'ainda não pagos' },
        ] : []}
      />
      <PageBody>
      <Card className="overflow-hidden">
        <SheetHeader title={loading ? 'Carregando pedidos' : `${formatNumero(visibleOrders.length)} ${visibleOrders.length === 1 ? 'pedido' : 'pedidos'}`}>
          <Segmented options={PERIODOS} value={periodo} onChange={setPeriodo} label="Período" testid="pedidos-periodo" />
          <WorkflowStatusFilter value={statusFilter} onChange={setStatusFilter} counts={statusCounts} />
        </SheetHeader>

      {/* Orders List */}
      {loading ? (
        <ul aria-hidden>
          {[1, 2, 3, 4].map((i) => (
            <li key={i} className="space-y-2 border-b border-border px-5 py-5">
              <div className="h-4 w-40 animate-pulse rounded bg-muted" />
              <div className="h-3 w-64 animate-pulse rounded bg-muted" />
            </li>
          ))}
        </ul>
      ) : visibleOrders.length === 0 ? (
        <div className="px-5 py-14">
          <p className="text-[15px] font-medium">Nenhum pedido encontrado</p>
          <p className="mt-1 text-sm text-gray-500">
            {statusFilter === 'all'
              ? 'Crie o primeiro pedido para começar.'
              : 'Nenhum pedido neste status. Ajuste o filtro ou crie um novo.'}
          </p>
          <Button onClick={() => navigate('/pedidos/novo')} className="mt-5" data-testid="create-first-order-button">
            <Plus className="h-4 w-4" />
            Criar pedido
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {visibleOrders.map((order) => {
            const statusConfig = getStatusConfig(order.status);
            // Workflow efetivo (helper compartilhado): pagamento confirmado com
            // workflow preso numa etapa inicial exibe "pagamento_confirmado".
            const wf = effectiveWorkflow(order);
            const temWorkflow = Boolean(wf);
            const isOwner = order.usuario_id === user?.id;
            // Belluno antiga com comprovante já anexado (antes do link) termina pelo comprovante.
            const isManual = FORMAS_MANUAIS.includes(order.forma_pagamento)
              || (order.forma_pagamento === 'belluno' && (order.comprovantes || []).length > 0);
            const comprovantes = order.comprovantes || [];
            const podeAnexar = (isOwner || user?.role === 'master' || user?.role === 'sales_ops')
              && ['aguardando_pagamento', 'rejeitado', 'comprovante_em_analise'].includes(wf);
            const historico = order.historico || [];
            const tinyPedidos = order.tiny_pedidos || [];
            const isPago = order.payment_status === 'confirmed';
            // Split: pedido com 2 pagamentos no mesmo pedido.
            const splitPartes = Array.isArray(order.pagamento_split) ? order.pagamento_split : [];
            const isSplit = splitPartes.length > 0;
            const splitPagos = splitPartes.filter((p) => p.pago).length;
            // Split com parte PIX/boleto: o fluxo de comprovante vale mesmo que
            // a forma do PEDIDO seja cartão (ex.: PIX + cartão) — sem isso o
            // vendedor não tinha onde anexar o comprovante da parte manual.
            const splitTemManual = splitPartes.some((p) => p.metodo === 'pix' || p.metodo === 'boleto');
            // Checkout já emitido e aguardando pagamento: a UI para de oferecer
            // "gerar" (evita 2 caminhos de cobrança) e passa a mostrar o estado.
            const isCancelado = wf === 'cancelado';
            const checkoutAtivo = !isPago && !isCancelado && (Boolean(order.checkout_token) || isSplit);
            // Última tentativa do cliente foi recusada? (só vale se ainda não pago
            // e o evento mais recente for uma recusa — se o vendedor regerou o
            // checkout depois, o evento novo é mais recente e isso volta a false).
            const ultimoEvento = [...(order.historico || [])]
              .sort((a, b) => new Date(a.em || 0) - new Date(b.em || 0))
              .slice(-1)[0];
            const ultimaTentativaRecusada = checkoutAtivo
              && /recusad|não autoriz|nao autoriz|not_authorized|falh/i.test(ultimoEvento?.obs || '');
            // O antifraude da Pagar.me reprovou a última tentativa: aviso em vermelho até o cliente tentar de novo. Vem do
            // estado do pedido, não do último evento (um alerta de checkout frio depois enterraria o aviso). O pedido
            // segue aberto (decisão de 2026-10-09).
            const reprovadoAntifraude = checkoutAtivo && ultimaTentativaReprovada(order);
            // Quem cobra (checkout e links da Belluno); o servidor confere de novo.
            const podeCobrar = ['comercial', 'master', 'administrativo', 'sales_ops'].includes(user?.role);
            const isBelluno = order.forma_pagamento === 'belluno';
            // Pedido pago não tem o que cobrar — o backend já recusa gerar
            // checkout ("Pedido já pago"), então o botão some da UI também.
            // Pedido em Belluno cobra pelo link da Belluno (que confirma o pedido sozinho ao ser pago): o do pedido
            // inteiro ou o de cada parte Belluno do split; nunca pelo checkout único da Pagar.me.
            // Financiamento nunca é cobrado pelo ERP: a financeira paga e o Financeiro registra o recebimento.
            const podeGerarCheckout = podeCobrar && !isPago && !isCancelado && order.forma_pagamento !== FINANCIAMENTO
              && (!isBelluno || podeCobrarBelluno(order));
            // Algum link esperando o cliente: o controle vira "Alterar forma".
            const cobrancaAtiva = checkoutAtivo || linkBellunoAtivo(order);
            // Link do pedido inteiro; com split valem os links das partes (na seção do split).
            const temLinkBelluno = Boolean(order.belluno_link?.transaction_id) && !isSplit;
            // Concluído = pago E despachado: nada pendente neste pedido.
            const isConcluido = isPago && wf === 'enviado_tiny';
            const podeEditar = (user?.role === 'comercial' && order.usuario_id === user.id) || user?.role === 'master' || user?.role === 'administrativo' || user?.role === 'sales_ops';
            const estornado = Number(order.valor_estornado) || 0;
            return (
              <div
                key={order.id}
                className="group relative"
                data-testid={`order-card-${order.numero_pedido}`}
              >
                {/* Botão Deletar (apenas MASTER) — discreto, ganha ênfase só no hover */}
                {user?.role === 'master' && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDeleteDialog(order);
                    }}
                    variant="ghost"
                    size="sm"
                    className="absolute right-3 top-3 z-10 h-8 w-8 p-0 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                    title="Deletar Pedido"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}

                {/* ===== Corpo do card: header + meta inline + ações (denso) ===== */}
                <div className="px-5 py-4">
                  {/* Header: número (âncora) + cliente à esquerda; chips de estado à direita */}
                  <div className="flex items-start justify-between gap-3 pr-7">
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2">
                        <h3 className="text-base font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
                          {order.numero_pedido}
                        </h3>
                        {isSplit && (
                          <span className="inline-flex items-center gap-1 text-[12.5px] text-gray-500">
                            <SplitSquareHorizontal className="h-3.5 w-3.5" />
                            2 pagamentos
                          </span>
                        )}
                        {order.numero_proposta && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/propostas?busca=${encodeURIComponent(order.numero_proposta)}`); }}
                            title="Abrir a proposta de origem"
                            className="inline-flex items-center gap-1 text-[12.5px] font-medium text-blue-600 hover:underline dark:text-blue-400"
                          >
                            <FileText className="h-3 w-3" />
                            de {order.numero_proposta}
                          </button>
                        )}
                        {isSplit && splitPagos > 0 && splitPagos < splitPartes.length && (
                          <span className="text-[12.5px] font-medium tabular-nums text-gray-500">
                            {splitPagos}/{splitPartes.length} pago
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 truncate">
                        {order.cliente_nome}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1">
                      {temWorkflow && <WorkflowChip wf={wf} pedido={order} />}
                      {estornado > 0 && (
                        <StatusDot tone="bad" className="text-[13px]">Estornado {formatBRL(estornado)}</StatusDot>
                      )}
                      {/* Vendido sem estoque livre na filial de saída, com confirmação do vendedor (encomenda) */}
                      {order.venda_sem_estoque?.length > 0 && <StatusDot tone="att">Venda sem estoque</StatusDot>}
                      {/* Fallback de status quando não há workflow */}
                      {!temWorkflow && (order.payment_status !== 'confirmed' || order.status === 'sent_to_tiny') && (
                        <StatusDot tone="off">{statusConfig.label}</StatusDot>
                      )}
                      {!temWorkflow && order.payment_status === 'confirmed' && <StatusDot tone="ok">Pago</StatusDot>}
                      {/* Segundo estado = SÓ o que o workflow não diz: a última tentativa
                          (recusada) ou o fato de o checkout existir. Split mostra as partes abaixo. */}
                      {linkBellunoAtivo(order) && !isPago && !isSplit && <StatusDot tone="run">Link Belluno gerado</StatusDot>}
                      {reprovadoAntifraude && <StatusDot tone="bad">Pagamento reprovado pelo antifraude</StatusDot>}
                      {checkoutAtivo && !isSplit && !reprovadoAntifraude && (
                        ultimaTentativaRecusada
                          ? <StatusDot tone="att">Tentativa recusada</StatusDot>
                          : <StatusDot tone="run">Checkout gerado</StatusDot>
                      )}
                    </div>
                  </div>

                  {/* Meta inline (quiet label:value) + Total como âncora ao fim da linha */}
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
                    <span className="text-slate-500 dark:text-slate-400">
                      {formatData(order.created_at)}
                      <span className="text-gray-500"> · {formatHora(order.created_at)}</span>
                    </span>
                    <span className="h-1 w-1 rounded-full bg-slate-300 dark:bg-slate-700" aria-hidden />
                    <span className="text-slate-500 dark:text-slate-400">
                      {order.itens?.length || 0} <span className="text-gray-500">item(ns)</span>
                    </span>
                    <span className="h-1 w-1 rounded-full bg-slate-300 dark:bg-slate-700" aria-hidden />
                    <span className="text-slate-500 dark:text-slate-400">
                      Frete <span className="font-medium text-slate-600 dark:text-slate-300 tabular-nums">{formatBRL(order.frete?.valor)}</span>
                    </span>
                    <span className="ml-auto flex items-baseline gap-1.5">
                      <span className="text-[12px] text-gray-500">Total</span>
                      <span className="text-xl font-medium tracking-[-0.02em] tabular-nums">
                        {formatBRL(order.total)}
                      </span>
                    </span>
                  </div>

                  {/* Split: uma linha fina por pagamento (valor + status + copiar) */}
                  {isSplit && (
                    <SplitSection
                      pedido={order}
                      partes={splitPartes}
                      onCopy={copyLink}
                      podeAnexar={podeAnexar}
                      uploading={uploadingId === order.id}
                      onAnexar={(p, f) => handleUploadComprovante(order, f, p)}
                      podeAgirBelluno={podeCobrar}
                      onBelluno={aplicarBelluno(order.id)}
                      onErroBelluno={() => fetchOrders(true)}
                    />
                  )}

                  {/* Ações à direita; num pedido concluído a linha não fica
                      vazia: o espaço livre vira a confirmação do pagamento
                      (que de outro modo só aparece abrindo o histórico). */}
                  {(podeEditar || podeGerarCheckout || isConcluido) && (
                    <div className="mt-3 flex items-center justify-between gap-3">
                      {isConcluido ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" />
                          {order.payment_confirmed_at
                            ? `Pago em ${formatDataHora(order.payment_confirmed_at)}`
                            : 'Pagamento confirmado'}
                        </span>
                      ) : (
                        <span aria-hidden />
                      )}
                      <div className="flex flex-wrap items-center justify-end gap-2">
                      {podeEditar && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/pedidos/editar/${order.id}`);
                          }}
                          variant="outline"
                          size="sm"
                        >
                          <Edit className="h-4 w-4" />
                          Editar pedido
                        </Button>
                      )}
                      {podeGerarCheckout && (
                        <>
                          {/* Checkout único ativo: copiar o link existente em vez
                              de gerar outro (o botão de gerar some). */}
                          {checkoutAtivo && !isSplit && (
                            <Button
                              onClick={(e) => { e.stopPropagation(); copyLink(checkoutUrlDe(order)); }}
                              variant="outline"
                              size="sm"
                            >
                              <Copy className="h-4 w-4" />
                              Copiar link
                            </Button>
                          )}
                          <GerarCheckoutControl
                            total={order.total || 0}
                            onSingle={() => (isBelluno ? handleGerarLinkBelluno(order) : handleGerarCheckout(order))}
                            onSplit={(partes) => handleGerarSplit(order, partes)}
                            partesAtuais={splitPartes}
                            habilitados={habilitados}
                            formaPedido={order.forma_pagamento}
                            parcelamentoPedido={order.parcelamento}
                            checkoutAtivo={cobrancaAtiva}
                            isSplit={isSplit}
                            rotulo={isBelluno ? 'Gerar link Belluno' : 'Gerar checkout'}
                            testid={isBelluno ? `gerar-link-belluno-${order.numero_pedido}` : `gerar-checkout-${order.numero_pedido}`}
                          />
                        </>
                      )}
                      </div>
                    </div>
                  )}
                </div>

                {/* ===== Workflow do hub: comprovantes, conferência, Tiny, histórico ===== */}
                {temWorkflow && (
                  <div className="mx-5 mb-5 space-y-3 rounded-[10px] bg-sheet2 p-4">
                    {/* Forma de pagamento (financiamento: a financeira e as parcelas que ela aprovou) */}
                    {order.forma_pagamento && (
                      <div className="flex items-center gap-2 text-sm">
                        <CreditCard className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                        <span className="text-slate-500 dark:text-slate-400">Pagamento</span>
                        <span className="font-medium text-gray-800 dark:text-slate-200">
                          {order.forma_pagamento === FINANCIAMENTO ? resumoFinanciamento(order) : nomeDaForma(order.forma_pagamento)}
                          {FORMAS_COM_LINK.includes(order.forma_pagamento) && order.parcelamento > 1 && ` (até ${order.parcelamento}x)`}
                        </span>
                      </div>
                    )}
                    {aguardandoFinanceira(order) && <StatusDot tone="run">Aguardando a financeira</StatusDot>}

                    {/* Link Belluno: estado, copiar, atualizar, cancelar */}
                    {temLinkBelluno && (
                      <EstadoLinkBelluno
                        pedido={order}
                        podeAgir={podeCobrar}
                        onChanged={aplicarBelluno(order.id)}
                        onErro={() => fetchOrders(true)}
                      />
                    )}

                    {/* Motivo de rejeição (volta pro vendedor) */}
                    {wf === 'rejeitado' && order.verificacao?.motivo_rejeicao && (
                      <div>
                        <StatusDot tone="bad" className="font-medium">Comprovante rejeitado pelo financeiro</StatusDot>
                        <p className="mt-1 pl-[15px] text-sm text-gray-700 dark:text-gray-300">{order.verificacao.motivo_rejeicao}</p>
                        <p className="mt-0.5 pl-[15px] text-[12.5px] text-gray-500">Anexe um novo comprovante para reenviar à conferência.</p>
                      </div>
                    )}

                    {/* Pagamento confirmado */}
                    {(wf === 'pagamento_confirmado' || wf === 'enviado_tiny') && order.verificacao?.verificado_por_nome && (
                      <StatusDot tone="ok">Pagamento confirmado por {order.verificacao.verificado_por_nome}</StatusDot>
                    )}

                    {/* Comprovantes (formas manuais ou split com parte PIX/boleto) */}
                    {(isManual || splitTemManual) && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                            <FileText className="h-4 w-4 text-blue-500" />
                            Comprovantes ({comprovantes.length})
                          </p>
                          {podeAnexar && !isSplit && (
                            <label
                              className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${
                                uploadingId === order.id
                                  ? 'bg-slate-100 text-slate-400 dark:bg-slate-800 cursor-not-allowed'
                                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20'
                              }`}
                            >
                              {uploadingId === order.id
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Upload className="h-4 w-4" />}
                              {uploadingId === order.id ? 'Enviando...' : 'Anexar comprovante'}
                              <input
                                type="file"
                                className="hidden"
                                accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,application/pdf"
                                disabled={uploadingId === order.id}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  handleUploadComprovante(order, f);
                                  e.target.value = '';
                                }}
                                data-testid={`upload-comprovante-${order.numero_pedido}`}
                              />
                            </label>
                          )}
                        </div>
                        {comprovantes.length > 0 && (
                          <div className="space-y-1.5">
                            {comprovantes.map((comp) => (
                              <div
                                key={comp.file_id}
                                className="flex items-center justify-between gap-2 p-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate" title={comp.filename}>{comp.filename}</p>
                                    <p className="text-[11px] text-slate-500">
                                      {comp.parte && (
                                        <span className="mr-1.5 rounded bg-slate-500/10 px-1.5 py-px font-medium text-slate-500 dark:text-slate-400">
                                          Pagamento {comp.parte}
                                        </span>
                                      )}
                                      {comp.uploaded_at && `${formatData(comp.uploaded_at)} ${formatHora(comp.uploaded_at)}`}
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10"
                                  disabled={viewingFileId === comp.file_id}
                                  onClick={() => handleViewComprovante(order, comp)}
                                >
                                  {viewingFileId === comp.file_id
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <Eye className="h-3.5 w-3.5" />}
                                  <span className="ml-1">Ver</span>
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        {comprovantes.length === 0 && podeAnexar && (
                          <p className="text-xs text-slate-500 italic">
                            {isSplit
                              ? 'Nenhum comprovante anexado. Anexe na linha do pagamento PIX/Boleto acima para o financeiro verificar.'
                              : 'Nenhum comprovante anexado. Anexe o comprovante para o financeiro verificar.'}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Pedidos criados no Tiny (split por filial) */}
                    {tinyPedidos.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                          <Truck className="h-4 w-4 text-blue-500" /> Pedidos no Tiny
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {tinyPedidos.map((tp, idx) => (
                            <StatusDot key={idx} tone={tp.status === 'criado' ? 'ok' : 'bad'} className="text-[13px]">
                              <span title={tp.erro || ''}>
                                {tp.filial_label || nomeFilial(tp.empresa_id)}: {tp.numero_tiny ? `pedido ${tp.numero_tiny}` : (tp.status === 'criado' ? 'criado' : 'erro')}
                              </span>
                            </StatusDot>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Histórico do pedido */}
                    {historico.length > 0 && (
                      <div>
                        <button
                          type="button"
                          onClick={() => toggleHistorico(order.id)}
                          className="text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1.5 transition-colors"
                        >
                          <History className="h-4 w-4" />
                          {expandedHistorico[order.id] ? 'Ocultar histórico' : `Ver histórico (${historico.length})`}
                        </button>
                        {expandedHistorico[order.id] && (
                          <div className="mt-2">
                            <HistoricoPedido historico={historico} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </Card>
      </PageBody>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => (open ? setDeleteDialogOpen(true) : handleCloseDeleteDialog())}
        title="Confirmar Exclusão de Pedido"
        description="Esta ação é irreversível. O pedido será excluído do sistema."
        confirmText="Confirmar Exclusão"
        onConfirm={handleDeleteOrder}
        loading={deleting}
        variant="destructive"
        details={
          orderToDelete
            ? [
                { label: 'Pedido', value: `#${orderToDelete.numero_pedido}` },
                { label: 'Cliente', value: orderToDelete.cliente_nome },
                {
                  label: 'Valor',
                  value: formatBRL(orderToDelete.valor_total ?? orderToDelete.total ?? 0),
                },
                ...(orderToDelete.numero_proposta
                  ? [{ label: 'Proposta', value: `${orderToDelete.numero_proposta} volta a ficar aberta` }]
                  : []),
                ...(numerosNoTiny(orderToDelete).length
                  ? [{
                    label: 'No Tiny',
                    value: numerosNoTiny(orderToDelete).length === 1
                      ? `pedido ${numerosNoTiny(orderToDelete)[0]} continua lá; cancele no Tiny`
                      : `pedidos ${numerosNoTiny(orderToDelete).join(', ')} continuam lá; cancele no Tiny`,
                  }]
                  : []),
              ]
            : null
        }
      />
    </div>
  );
};

export default Orders;
